import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiClient as api } from '../services/api';
import { supabase } from '../supabaseClient';
import { FiLink, FiCopy, FiX, FiCheck } from 'react-icons/fi';
import './FormCodeGenerator.css';

interface FormCodeGeneratorProps {
  onCodeGenerated?: (code: string, formUrl: string) => void;
}

interface FormCodeGeneratorRef {
  refreshData: () => void;
}

interface IntakeForm {
  id: string;
  title: string;
  case_type: string;
  hasActiveSessions?: boolean;
}

interface FormSession {
  id: string;
  code: string;
  formId: string;
  formTitle: string;
  clientName?: string; // Client name for this form session
  caseType: string;
  isSubmitted: boolean;
  submittedAt: string | null;
  lastUpdated: string;
  createdAt: string;
  expiresAt: string;
  isActive?: boolean;
  deactivatedAt?: string | null;
  deactivatedReason?: string | null;
}

const FormCodeGenerator: React.FC<FormCodeGeneratorProps> = ({ onCodeGenerated }) => {
  const { user } = useAuth();
  const [selectedForm, setSelectedForm] = useState('');
  const [availableForms, setAvailableForms] = useState<IntakeForm[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [sessions, setSessions] = useState<FormSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [uniqueUsers, setUniqueUsers] = useState<string[]>([]);
  
  // Search and slider functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [latestCount, setLatestCount] = useState(10);

  const [copiedSessionId, setCopiedSessionId] = useState<string | null>(null);
  const handleCopyLink = async (url: string, sessionId: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSessionId(sessionId);

      // reset after 3s
      setTimeout(() => setCopiedSessionId(null), 3000);
    } catch (err) {
      console.error("Failed to copy!", err);
    }
  };

  useEffect(() => {
    loadAvailableForms();
    loadSessions();
  }, []);

  const loadAvailableForms = async () => {
    try {
      console.log('🔍 Loading available forms...');
      const response = await api.getIntakeForms();
      console.log('📡 API Response:', response);
      
      // Also load form sessions to check actual session status
      const sessionsResponse = await api.getFormSessions();
      console.log('Sessions response for form status check:', sessionsResponse);
      
      let allSessions: any[] = [];
      if (Array.isArray(sessionsResponse)) {
        allSessions = sessionsResponse;
      } else if (sessionsResponse && sessionsResponse.sessions) {
        allSessions = sessionsResponse.sessions;
      }
      
      // Handle both response formats: direct array or {success: true, forms: [...]}
      let forms = [];
      if (Array.isArray(response)) {
        console.log('✅ Forms loaded (direct array):', response);
        forms = response;
      } else if (response.success && response.forms) {
        console.log('✅ Forms loaded (success object):', response.forms);
        forms = response.forms;
      } else {
        console.log('❌ Unexpected response format:', response);
        return;
      }
      
      // Add session-based status to each form
      const formsWithStatus = forms.map((form: any) => {
        // Check if this form has any active sessions
        const hasActiveSessions = allSessions.some((session: any) => {
          const sessionFormId = session.formId || session.form_id;
          // A session is active if isActive is not explicitly false
          // Submitted forms should still be considered active until manually expired or timed out
          const isSessionActive = session.isActive !== false;
          return sessionFormId === form.id && isSessionActive;
        });
        
        return {
          ...form,
          hasActiveSessions
        };
      });
      
      setAvailableForms(formsWithStatus);
    } catch (error) {
      console.error('❌ Error loading forms:', error);
    }
  };

  const loadSessions = async () => {
    try {
      console.log('🔄 Loading sessions for user:', user?.id);
      const response = await api.getFormSessions();
      console.log('📡 Sessions API Response:', response);
      
      let allSessions: FormSession[] = [];
      
      // Handle both response formats
      if (Array.isArray(response)) {
        console.log('✅ Sessions loaded (direct array):', response.length, 'sessions');
        allSessions = response;
      } else if (response && response.success && response.sessions) {
        console.log('✅ Sessions loaded (success object):', response.sessions.length, 'sessions');
        allSessions = response.sessions;
      } else if (response && response.sessions) {
        console.log('✅ Sessions loaded (fallback):', response.sessions.length, 'sessions');
        allSessions = response.sessions;
      } else {
        console.log('❌ Unexpected sessions response format:', response);
        console.log('Response type:', typeof response);
        console.log('Response keys:', response ? Object.keys(response) : 'null/undefined');
      }
      
      // Log all session details for debugging
      console.log('🔍 All sessions details:');
      allSessions.forEach((session, index) => {
        console.log(`Session ${index + 1}:`, {
          id: session.id,
          code: session.code,
          formTitle: session.formTitle,
          clientName: session.clientName,
          isActive: session.isActive,
          isSubmitted: session.isSubmitted,
          createdAt: session.createdAt,
          expiresAt: session.expiresAt
        });
      });
      
      // Extract unique users from clientName field
      const users = new Set<string>();
      allSessions.forEach(session => {
        console.log('📝 Processing session:', session.formTitle, 'Client:', session.clientName);
        if (session.clientName && session.clientName.trim().length > 0) {
          const userName = session.clientName.trim();
          console.log('👤 Using client name:', userName);
          users.add(userName);
        } else {
          console.log('⚠️ Session has no client name, skipping:', session.formTitle);
        }
      });
      
      const uniqueUsersList = Array.from(users).sort();
      console.log('👥 Unique users found:', uniqueUsersList);
      setUniqueUsers(uniqueUsersList);
      setSessions(allSessions);
    } catch (error) {
      console.error('❌ Error loading sessions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateCode = async () => {
    if (!selectedForm) {
      alert('Please select a form type');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await api.generateFormCode(selectedForm);

      if (response.success) {
        const { code, formUrl } = response;
        setGeneratedCode(code);
        setGeneratedUrl(formUrl);
        
        // Copy to clipboard
        navigator.clipboard.writeText(formUrl);
        
        // Notify parent component
        if (onCodeGenerated) {
          onCodeGenerated(code, formUrl);
        }
        
        // Show success dialog instead of alert
        setShowSuccessDialog(true);
        
        // Refresh both sessions list and available forms after a short delay to ensure backend processing is complete
        setTimeout(() => {
          loadSessions();
          loadAvailableForms();
        }, 500);
      }
    } catch (error) {
      console.error('Error generating code:', error);
      alert('Failed to generate code. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const expireFormSession = async (sessionId: string, sessionCode: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to expire/close the form session with code "${sessionCode}"? This action cannot be undone and the client will no longer be able to access this form.`
    );
    
    if (!confirmed) return;

    try {
      const response = await api.expireFormSession(sessionId);
      
      if (response.success) {
        alert('Form session has been successfully expired/closed.');
        // Refresh both sessions list and available forms to show updated status with delay
        setTimeout(() => {
          loadSessions();
          loadAvailableForms();
        }, 300);
      } else {
        alert('Failed to expire form session. Please try again.');
      }
    } catch (error) {
      console.error('Error expiring form session:', error);
      alert('Failed to expire form session. Please try again.');
    }
  };

  const getDaysLeft = (expiresAt: string) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffTime = expires.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Success Dialog Component
  const SuccessDialog = () => {
    if (!showSuccessDialog) return null;

    return (
      <div className="success-dialog-overlay">
        <div className="success-dialog">
          <div className="success-dialog-header">
            <h3>✅ Code Generated Successfully!</h3>
            <button 
              onClick={() => setShowSuccessDialog(false)}
              className="close-btn"
            >
              ×
            </button>
          </div>
          
          <div className="success-dialog-content">
            
            <div className="url-display">
              <label>Form URL:</label>
              <div className="url-box">
                <span className="url-text">{generatedUrl}</span>
                <button 
                  onClick={() => copyToClipboard(generatedUrl)}
                  className="copy-url-btn"
                >
                  📋 Copy Link
                </button>
              </div>
            </div>
            
            <div className="instructions">
              <h4>Next Steps:</h4>
              <ul>
                <li>✅ Form link copied to clipboard</li>
                <li>📧 Send the link to your client</li>
                <li>👀 Track progress in "Active Form Sessions" below</li>
                <li>📱 Client can access form using the shareable link</li>
              </ul>
            </div>
          </div>
          
          <div className="success-dialog-actions">
            <button 
              onClick={() => {
                setShowSuccessDialog(false);
                // Refresh both sessions and available forms when dialog is closed
                loadSessions();
                loadAvailableForms();
              }}
              className="close-dialog-btn"
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="form-code-generator">
      <h2>Generate Form Access Codes</h2>
      
      <div className="generate-section">
        <h3>Create New Form Code</h3>
        
        <div className="form-group">
          <label>Form Type:</label>
          <select 
            value={selectedForm} 
            onChange={(e) => setSelectedForm(e.target.value)}
            className="form-select"
          >
            <option value="">Select Form Type</option>
            {availableForms.map(form => (
              <option 
                key={form.id} 
                value={form.id}
                style={{
                  color: !form.hasActiveSessions ? '#dc3545' : '#333',
                  fontStyle: !form.hasActiveSessions ? 'italic' : 'normal'
                }}
              >
                {form.title} ({form.case_type}) {!form.hasActiveSessions ? ' - EXPIRED' : ''}
              </option>
            ))}
          </select>
        </div>
        
        <button 
          onClick={generateCode} 
          disabled={!selectedForm || isGenerating}
          className="generate-btn"
        >
          {isGenerating ? 'Generating...' : 'Generate Code'}
        </button>
      </div>
      
      <div className="sessions-section">
        <div className="sessions-header">
          <h3>Active Form Sessions</h3>
          
          {/* Search Bar */}
          <div className="search-bar-container" style={{ marginBottom: "20px" }}>
            <input
              type="text"
              placeholder="Search by client name or form title..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                fontSize: "14px",
                backgroundColor: "#fff"
              }}
            />
          </div>

          {/* Slider to show latest N forms */}
          <div className="latest-slider-container" style={{ margin: "10px 0 20px 0" }}>
            <label htmlFor="latest-slider" style={{ marginRight: "10px", color: "#000", fontSize: "14px" }}>
              Show latest {latestCount} sessions:
            </label>
            <input
              type="range"
              id="latest-slider"
              min={1}
              max={50}
              value={latestCount}
              onChange={(e) => setLatestCount(Number(e.target.value))}
              style={{ verticalAlign: "middle" }}
            />
          </div>
        </div>
        
        {(() => {
          // Filter sessions based on search and slider
          console.log('🔍 Filtering sessions with search:', searchQuery);
          console.log('📊 Total sessions:', sessions.length);
          
          let filteredSessions = sessions;
          
          // First filter by search query
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filteredSessions = filteredSessions.filter(session => {
              const clientName = (session.clientName || '').toLowerCase();
              const formTitle = (session.formTitle || '').toLowerCase();
              return clientName.includes(query) || formTitle.includes(query);
            });
            console.log('✅ Filtered by search:', filteredSessions.length);
          }
          
          // Show only active sessions (not expired and not deactivated)
          console.log('🟢 Showing only active sessions');
          filteredSessions = filteredSessions.filter(session => {
            // Check if session is deactivated
            const isActiveValue = session.isActive;
            const isDeactivated = isActiveValue === false;
            
            // Check if session is expired
            const isExpired = new Date(session.expiresAt) < new Date();
            
            // Session is truly active if: not deactivated AND not expired
            const isTrulyActive = !isDeactivated && !isExpired;
            
            console.log(`🔍 Session "${session.code}" active check:`, {
              isTrulyActive,
              isDeactivated,
              isExpired,
              isActiveValue,
              expiresAt: session.expiresAt,
              formTitle: session.formTitle
            });
            return isTrulyActive;
          });
          
          // Sort by creation date (newest first)
          filteredSessions = filteredSessions.sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
          
          // Apply slider limit
          console.log(`🔢 Applying slider limit: ${latestCount} sessions`);
          filteredSessions = filteredSessions.slice(0, latestCount);
          
          console.log('✅ Final filtered sessions:', filteredSessions.length);
          
          if (filteredSessions.length === 0) {
            return (
              <p className="no-sessions">
                {searchQuery 
                  ? `No form sessions found for "${searchQuery}".`
                  : 'No active form sessions.'
                }
              </p>
            );
          }
          
          return (
            <div className="sessions-list">
              {filteredSessions.map(session => {
              // Determine session status (consistent with filtering logic)
              const getSessionStatus = () => {
                if (session.isSubmitted) {
                  return { class: 'submitted', text: 'SUBMITTED', color: '#10b981' };
                } else if (session.isActive === false) {
                  return { class: 'deactivated', text: 'DEACTIVATED', color: '#6b7280' };
                } else {
                  // Since we've already filtered out expired sessions, 
                  // any remaining sessions should be truly active
                  return { class: 'active', text: 'ACTIVE', color: '#3b82f6' };
                }
              };
              
              const status = getSessionStatus();
              const isExpired = new Date(session.expiresAt) < new Date();
              
              return (
                <div 
                  key={session.id} 
                  className="session-row"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 20px",
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    marginBottom: "12px",
                    transition: "all 0.2s ease",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <h4 style={{ color: "#000", fontWeight: "600", fontSize: "16px", margin: "0 0 4px 0" }}>
                      {session.formTitle}
                    </h4>
                    <p style={{ color: "#6b7280", fontSize: "14px", margin: 0 }}>
                      Code: {session.code} • {session.caseType} • {session.clientName ? `Client: ${session.clientName}` : 'No client name'}
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                    <span
                      style={{
                        background: status.color,
                        color: "white",
                        padding: "4px 12px",
                        borderRadius: "20px",
                        fontSize: "11px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                      }}
                    >
                      {status.text}
                    </span>
                    <button
                      onClick={() => window.open(`${window.location.origin}/?code=${session.code}`, "_blank")}
                      style={{
                        background: "#10b981",
                        border: "none",
                        color: "#fff",
                        padding: "8px 16px",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s ease"
                      }}
                    >
                      Open Link
                    </button>
                    <button
                      onClick={() => handleCopyLink(`${window.location.origin}/?code=${session.code}`, session.id)}
                      style={{
                        background: "#3b82f6",
                        border: "none",
                        color: "#fff",
                        padding: "8px 16px",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s ease"
                      }}
                    >
                      {copiedSessionId === session.id ? "Copied!" : "Copy Link"}
                    </button>
                    <button 
                      onClick={() => expireFormSession(session.id, session.code)}
                      style={{
                        background: "#dc3545",
                        border: "none",
                        color: "#fff",
                        padding: "8px 16px",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.2s ease"
                      }}
                    >
                      Close Form
                    </button>
                  </div>
                </div>
              );
            })}
            </div>
          );
        })()}
      </div>

      {/* Success Dialog */}
      <SuccessDialog />
    </div>
  );
};

export default FormCodeGenerator; 